import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { Camera, CameraOff, Search, CheckCircle2, UserCheck, Flashlight, SwitchCamera, RefreshCw, Wine, Ticket, Home, LayoutDashboard, ScanLine, ClipboardList, ChevronRight, Users, ShieldCheck, X } from "lucide-react";
import { api, formatError, avatarSrc, fmtDate, fmtDateTime } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import RoleBadge from "../../components/RoleBadge";
import DrinkScanner from "../DrinkScanner";

const CAT_LABEL = { classes: "Classes", socials: "Socials", events: "Events" };

function ScanToast({ data, onClose }) {
  const [dx, setDx] = useState(0);
  const start = useRef(null);
  useEffect(() => { const t = setTimeout(onClose, 5000); return () => clearTimeout(t); }, [data, onClose]);
  return (
    <div className="fixed bottom-[max(env(safe-area-inset-bottom),1rem)] left-1/2 -translate-x-1/2 z-[300] w-[min(24rem,calc(100vw-2rem))]" data-testid="scan-success-toast">
      <div
        onTouchStart={(e) => { start.current = e.touches[0].clientX; }}
        onTouchMove={(e) => { if (start.current != null) setDx(e.touches[0].clientX - start.current); }}
        onTouchEnd={() => { if (Math.abs(dx) > 70) onClose(); else setDx(0); start.current = null; }}
        style={{ transform: `translateX(${dx}px)`, opacity: 1 - Math.min(Math.abs(dx) / 200, 0.7) }}
        className="relative rounded-2xl bg-[#0B2E1A] border border-[#4ADE80]/50 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.8)] p-4 pr-10 flex items-center gap-3 transition-transform">
        <div className="w-10 h-10 rounded-full bg-[#4ADE80]/20 flex items-center justify-center shrink-0"><CheckCircle2 size={20} className="text-[#4ADE80]" /></div>
        <div className="min-w-0">
          <p className="font-semibold text-sm truncate">{data.name} scanned in</p>
          <p className="text-[#A19DB0] text-xs">Swipe to dismiss, or tap ✕</p>
        </div>
        <button onClick={onClose} data-testid="scan-toast-close" aria-label="Dismiss" className="absolute top-2.5 right-2.5 text-[#A19DB0] hover:text-white"><X size={17} /></button>
      </div>
    </div>
  );
}
const sgtDay = (iso) => {
  if (!iso) return "";
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Singapore", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso));
  return p; // YYYY-MM-DD
};
const todaySGT = () => sgtDay(new Date().toISOString());
const addDaysSGT = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return sgtDay(d.toISOString()); };

export default function AdminScanner() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";

  const [view, setView] = useState("landing"); // landing | occurrence
  const [when, setWhen] = useState("upcoming");
  const [catFilter, setCatFilter] = useState("all");
  const [drinkMode, setDrinkMode] = useState(false);
  const [dateFilter, setDateFilter] = useState("all");
  const [categories, setCategories] = useState([]);
  const [catLabels, setCatLabels] = useState({});
  const [occurrences, setOccurrences] = useState([]);

  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [roster, setRoster] = useState(null);
  const [rosterQuery, setRosterQuery] = useState("");
  const [holders, setHolders] = useState(null);
  const [holdersQuery, setHoldersQuery] = useState("");
  const [staffOnly, setStaffOnly] = useState(false);
  const [live, setLive] = useState({ signups: 0, checked_in: 0 });

  // scanner
  const [scanning, setScanning] = useState(false);
  const [facing, setFacing] = useState("environment");
  const [torchOn, setTorchOn] = useState(false);
  const [recent, setRecent] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [memberPasses, setMemberPasses] = useState(null);
  const [scanToast, setScanToast] = useState(null);
  const [passTab, setPassTab] = useState("passes");
  const scannerRef = useRef(null);
  const lastRef = useRef({ code: "", t: 0 });

  // staff access
  const [staffOpen, setStaffOpen] = useState(false);
  const [staff, setStaff] = useState([]);

  const loadOccurrences = useCallback(() => {
    api.get(`/admin/occurrences?when=${when}`).then((r) => { setCategories(r.data.categories || []); setCatLabels(r.data.category_labels || {}); setOccurrences(r.data.occurrences || []); }).catch(() => {});
  }, [when]);
  useEffect(() => { loadOccurrences(); }, [loadOccurrences]);
  useEffect(() => () => { stopInternal(); }, []); // eslint-disable-line

  const sessionObj = useMemo(() => (selected ? { ref_type: selected.ref_type, ref_id: selected.ref_id } : null), [selected]);

  // stop camera when leaving scanner tab / occurrence view
  useEffect(() => { if (!(view === "occurrence" && tab === "scanner")) stop(); }, [view, tab]); // eslint-disable-line

  // Auto-start the camera for admin / super admin when they open the scanner tab, if permission was already granted
  useEffect(() => {
    if (view === "occurrence" && tab === "scanner" && !scanning && ["admin", "super_admin"].includes(user?.role)) {
      (async () => {
        try {
          if (navigator.permissions?.query) {
            const st = await navigator.permissions.query({ name: "camera" });
            if (st.state === "granted") start();
          }
        } catch (e) { /* Permissions API / camera name unsupported — leave manual start */ }
      })();
    }
    // eslint-disable-next-line
  }, [view, tab]);

  const filtered = useMemo(() => {
    const t = todaySGT(), tm = addDaysSGT(1), wk = addDaysSGT(7);
    return occurrences.filter((o) => {
      if (catFilter !== "all" && o.category !== catFilter) return false;
      if (dateFilter === "all") return true;
      const d = sgtDay(o.starts_at);
      if (dateFilter === "today") return d === t;
      if (dateFilter === "tomorrow") return d === tm;
      if (dateFilter === "week") return d >= t && d <= wk;
      return true;
    });
  }, [occurrences, catFilter, dateFilter]);

  const openOccurrence = (o) => { setSelected(o); setTab("dashboard"); setView("occurrence"); setRecent([]); setMemberPasses(null); setLive({ signups: o.signups || 0, checked_in: o.checked_in || 0 }); };
  const goHome = () => { stop(); setView("landing"); setSelected(null); setStats(null); setRoster(null); loadOccurrences(); };

  // dashboard + doorlist loaders
  const loadStats = useCallback(async () => {
    if (!sessionObj) return;
    try { const r = await api.get(`/admin/occurrence-stats?ref_type=${sessionObj.ref_type}&ref_id=${sessionObj.ref_id}`); setStats(r.data); setLive({ signups: r.data.signups, checked_in: r.data.checked_in }); } catch (e) { console.error("Load occurrence stats failed", e); }
  }, [sessionObj]);
  const refreshRoster = useCallback(async () => {
    if (!sessionObj) { setRoster(null); return; }
    try { const r = await api.get(`/admin/roster?ref_type=${sessionObj.ref_type}&ref_id=${sessionObj.ref_id}`); setRoster(r.data); setLive({ signups: r.data.expected_count, checked_in: r.data.present_count }); } catch (e) { console.error("Load roster failed", e); }
  }, [sessionObj]);
  useEffect(() => { if (view === "occurrence" && tab === "dashboard") loadStats(); }, [view, tab, loadStats]);
  useEffect(() => { if (view === "occurrence" && tab === "doorlist") refreshRoster(); }, [view, tab, refreshRoster]);
  const loadHolders = useCallback(async () => {
    if (!selected) return;
    try { const r = await api.get(`/admin/social-holders/${selected.ref_type}/${selected.ref_id}`); setHolders(r.data); } catch (e) { console.error("Load holders failed", e); }
  }, [selected]);
  useEffect(() => { if (view === "occurrence" && tab === "holders") loadHolders(); }, [view, tab, loadHolders]);
  const toggleHolder = async (uid, name, currentlyMarked) => {
    try {
      if (currentlyMarked) await api.delete(`/admin/checkin?ref_type=${selected.ref_type}&ref_id=${selected.ref_id}&user_id=${uid}`);
      else await api.post("/admin/checkin", { user_id: uid, ref_type: selected.ref_type, ref_id: selected.ref_id });
      toast.success(currentlyMarked ? `Unmarked ${name}` : `Marked ${name}`);
      loadHolders();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const doCheckin = async (userId, nameHint) => {
    if (!sessionObj) return;
    try {
      const r = await api.post("/admin/checkin", { user_id: userId, ...sessionObj });
      toast.success(`${r.data.member_name} checked in`, { description: r.data.guest ? "Guest — no booking" : "Checked in" });
      setScanToast({ name: r.data.member_name, ts: Date.now() });
      setRecent((prev) => [{ name: r.data.member_name, at: new Date().toISOString(), guest: r.data.guest }, ...prev].slice(0, 20));
      setLive((l) => ({ ...l, checked_in: l.checked_in + 1 }));
      refreshRoster(); loadStats();
    } catch (e) { toast.error(formatError(e.response?.data?.detail), { description: nameHint }); }
  };
  const unCheckin = async (userId, nameHint) => {
    if (!sessionObj) return;
    try {
      await api.delete(`/admin/checkin?ref_type=${sessionObj.ref_type}&ref_id=${sessionObj.ref_id}&user_id=${userId}`);
      toast.success(`${nameHint || "Guest"} check-in undone`);
      setLive((l) => ({ ...l, checked_in: Math.max(0, l.checked_in - 1) }));
      refreshRoster(); loadStats();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const loadPasses = async (uid) => {
    try { const r = await api.post("/admin/pass-scan", { uid }); setMemberPasses(r.data); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const redeemDrink = async (pid) => {
    try { await api.post(`/admin/pass/${pid}/redeem-drink`); toast.success("Drink redeemed"); if (memberPasses) loadPasses(memberPasses.member.id); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const passCheckin = async (pid) => {
    try { await api.post(`/admin/pass/${pid}/checkin`, sessionObj || {}); toast.success("Checked in"); setScanToast({ name: memberPasses?.member?.name || "Member", ts: Date.now() }); if (memberPasses) loadPasses(memberPasses.member.id); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const passUndo = async (pid) => {
    try { await api.post(`/admin/pass/${pid}/undo-checkin`); toast.success("Pass returned to member"); if (memberPasses) loadPasses(memberPasses.member.id); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const onDecode = (text) => {
    const now = Date.now();
    if (lastRef.current.code === text && now - lastRef.current.t < 3000) return;
    lastRef.current = { code: text, t: now };
    let data;
    try { data = JSON.parse(text); } catch { toast.error("Unrecognized QR code"); return; }
    if (data.uid) { if (sessionObj) doCheckin(data.uid, data.name); loadPasses(data.uid); return; }
    toast.error("Not a member QR code");
  };

  const startWith = async (f) => {
    try {
      const s = new Html5Qrcode("reader");
      scannerRef.current = s;
      await s.start({ facingMode: f }, { fps: 10, qrbox: (w, h) => { const m = Math.floor(Math.min(w, h) * 0.8); return { width: m, height: m }; }, aspectRatio: 0.6 }, onDecode, () => {});
      setScanning(true); setTorchOn(false);
    } catch (e) { toast.error("Could not start camera. Use manual check-in instead."); }
  };
  const start = () => startWith(facing);
  const stopInternal = async () => {
    const s = scannerRef.current;
    if (s) { try { await s.stop(); await s.clear(); } catch (e) { /* noop */ } scannerRef.current = null; }
  };
  const stop = async () => { await stopInternal(); setScanning(false); setTorchOn(false); };
  const switchCamera = async () => {
    const next = facing === "environment" ? "user" : "environment";
    setFacing(next);
    if (scanning) { await stopInternal(); await startWith(next); }
  };
  const toggleTorch = async () => {
    if (!scannerRef.current) return;
    try { await scannerRef.current.applyVideoConstraints({ advanced: [{ torch: !torchOn }] }); setTorchOn(!torchOn); }
    catch (e) { toast.error("Torch isn't supported on this camera"); }
  };
  const search = async (v) => {
    setQ(v);
    if (v.length < 2) { setResults([]); return; }
    try { const r = await api.get(`/admin/members?q=${encodeURIComponent(v)}`); setResults(r.data); } catch (e) { console.error("Member search failed", e); }
  };

  const openStaff = async () => { setStaffOpen(true); try { const r = await api.get("/admin/staff"); setStaff(r.data); } catch (e) { toast.error("Could not load staff"); } };
  const toggleStaffCat = (sid, cat) => setStaff((list) => list.map((s) => s.id === sid ? { ...s, scanner_access: s.scanner_access.includes(cat) ? s.scanner_access.filter((c) => c !== cat) : [...s.scanner_access, cat] } : s));
  const saveStaff = async (s) => {
    try { await api.put(`/admin/staff/${s.id}/access`, { categories: s.scanner_access }); toast.success(`${s.name}'s access saved`); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const CatBadge = ({ c }) => <span className="text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 bg-white/10 text-[#c79bff]">{CAT_LABEL[c] || c}</span>;

  // ---------------- Landing ----------------
  if (view === "landing") {
    return (
      <div>
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h2 className="font-display text-3xl font-bold">Event Scanner</h2>
            <p className="text-[#A19DB0] text-sm mt-1">Pick an event or class to open its dashboard, scanner and door list.</p>
          </div>
          {isSuper && <button onClick={openStaff} data-testid="staff-access-btn" className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm shrink-0"><ShieldCheck size={16} className="text-[#D4AF37]" /> Staff access</button>}
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="inline-flex rounded-full bg-white/5 border border-white/10 p-1">
            <button onClick={() => setDrinkMode(false)} data-testid="scanner-mode-events" className={`flex items-center gap-1.5 px-5 py-1.5 rounded-full text-sm font-semibold transition-colors ${!drinkMode ? "bg-[#D4AF37] text-black" : "text-[#A19DB0]"}`}><ScanLine size={15} /> Events</button>
            <button onClick={() => setDrinkMode(true)} data-testid="scanner-mode-drinks" className={`flex items-center gap-1.5 px-5 py-1.5 rounded-full text-sm font-semibold transition-colors ${drinkMode ? "bg-[#D4AF37] text-black" : "text-[#A19DB0]"}`}><Wine size={15} /> Drinks</button>
          </div>
          {!drinkMode && (
            <div className="inline-flex rounded-full bg-white/5 border border-white/10 p-1">
              {["upcoming", "past"].map((w) => (
                <button key={w} onClick={() => setWhen(w)} data-testid={`scanner-when-${w}`} className={`px-5 py-1.5 rounded-full text-sm font-semibold capitalize transition-colors ${when === w ? "bg-[#D4AF37] text-black" : "text-[#A19DB0]"}`}>{w}</button>
              ))}
            </div>
          )}
        </div>

        {!drinkMode && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <select value={catFilter} data-testid="scanner-cat-select" onChange={(e) => setCatFilter(e.target.value)}
            className="bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-[#D4AF37] min-w-[180px]">
            <option value="all" className="bg-[#140F1E]">All</option>
            {categories.map((c) => <option key={c} value={c} className="bg-[#140F1E]">{catLabels[c] || CAT_LABEL[c] || c}</option>)}
          </select>
          <div className="flex flex-wrap gap-2 ml-auto">
            {[["all", "All dates"], ["today", "Today"], ["week", "This week"]].map(([v, l]) => (
              <button key={v} onClick={() => setDateFilter(v)} data-testid={`scanner-date-${v}`} className={`rounded-full px-4 py-1.5 text-sm border ${dateFilter === v ? "bg-[#D4AF37]/15 border-[#D4AF37]/40 text-[#D4AF37]" : "bg-white/5 border-white/10 text-[#A19DB0]"}`}>{l}</button>
            ))}
          </div>
        </div>
        )}

        {!drinkMode && (<>

        {filtered.length === 0 ? (
          <div className="text-center py-16 text-[#A19DB0]" data-testid="no-occurrences"><ClipboardList size={40} className="mx-auto mb-3 opacity-50" /><p>No {when} sessions match these filters.</p></div>
        ) : (
          <div className="space-y-2" data-testid="occurrence-list">
            {filtered.map((o) => (
              <button key={`${o.ref_type}:${o.ref_id}`} onClick={() => openOccurrence(o)} data-testid={`occurrence-card-${o.ref_id}`} className="w-full text-left flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 hover:border-[#D4AF37]/40 transition-colors relative overflow-hidden">
                <span className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: o.color || "#9D4EDD" }} />
                <div className="min-w-0 flex-1 pl-1">
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-display text-base font-semibold">{fmtDateTime(o.starts_at)}</span><CatBadge c={o.category} /></div>
                  <p className="text-[#A19DB0] text-xs mt-0.5 truncate">{o.title}{o.subtitle ? ` · ${o.subtitle}` : ""}</p>
                </div>
                <div className="flex items-center gap-3 text-xs shrink-0">
                  <span className="flex items-center gap-1 text-[#A19DB0]"><Users size={13} /> {o.signups}</span>
                  <span className="flex items-center gap-1 text-[#9D4EDD]"><CheckCircle2 size={13} /> {o.checked_in}</span>
                  <ChevronRight size={16} className="text-[#A19DB0]" />
                </div>
              </button>
            ))}
          </div>
        )}
        </>)}

        {drinkMode && <div className="mt-2" data-testid="scanner-drink-embed"><DrinkScanner embedded /></div>}

        {staffOpen && (
          <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setStaffOpen(false)} data-testid="staff-access-modal">
            <div className="bg-[#140F1E] border border-white/10 rounded-3xl p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4"><h3 className="font-display text-xl font-semibold">Staff scanner access</h3><button onClick={() => setStaffOpen(false)} className="text-[#A19DB0]"><X size={18} /></button></div>
              <div className="space-y-3">
                {staff.map((s) => (
                  <div key={s.id} data-testid={`staff-access-${s.id}`} className="rounded-2xl bg-white/5 border border-white/10 p-4">
                    <p className="font-medium">{s.name} <span className="text-[#A19DB0] text-xs">· {s.role}</span></p>
                    <p className="text-[#A19DB0] text-xs">{s.email}</p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {SCANNER_CATS.map((c) => (
                        <label key={c} data-testid={`staff-cat-${s.id}-${c}`} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm cursor-pointer border ${s.scanner_access.includes(c) ? "bg-[#D4AF37]/15 border-[#D4AF37]/40 text-[#D4AF37]" : "bg-white/5 border-white/10 text-[#A19DB0]"}`}>
                          <input type="checkbox" className="hidden" checked={s.scanner_access.includes(c)} onChange={() => toggleStaffCat(s.id, c)} />
                          {CAT_LABEL[c]}
                        </label>
                      ))}
                      <button onClick={() => saveStaff(s)} data-testid={`save-staff-access-${s.id}`} className="ml-auto rounded-full bg-[#D4AF37] text-black font-semibold px-4 py-1.5 text-sm">Save</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---------------- Occurrence view ----------------
  return (
    <div>
      {scanToast && <ScanToast data={scanToast} onClose={() => setScanToast(null)} />}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={goHome} data-testid="scanner-home" className="flex items-center gap-1.5 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm hover:bg-white/10"><Home size={16} /> Home</button>
        <div className="min-w-0"><h2 className="font-display text-2xl font-bold truncate">{selected?.title}</h2><p className="text-[#A19DB0] text-xs">{fmtDateTime(selected?.starts_at)} · {CAT_LABEL[selected?.category] || selected?.category}</p></div>
        <span key={live.checked_in} data-testid="live-count" className="ml-auto shrink-0 flex items-center gap-1.5 rounded-full bg-[#9D4EDD]/20 border border-[#9D4EDD]/40 text-[#c79bff] px-3.5 py-1.5 text-sm font-semibold animate-in fade-in zoom-in duration-300">
          <CheckCircle2 size={15} /> <span className="text-white">{live.checked_in}</span> / {live.signups} in
        </span>
      </div>

      <div className="inline-flex rounded-full bg-white/5 border border-white/10 p-1 mb-6">
        {[["dashboard", LayoutDashboard, "Dashboard"], ["scanner", ScanLine, "Scanner"], ...(selected?.has_drinks ? [["drinks", Wine, "Drinks"]] : []), ["doorlist", ClipboardList, "Doorlist"], ...(isSuper && selected?.category === "socials" ? [["holders", Ticket, "Pass holders"]] : [])].map(([v, Icon, label]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`occ-tab-${v}`} className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === v ? "bg-[#D4AF37] text-black" : "text-[#A19DB0]"}`}><Icon size={15} /> {label}</button>
        ))}
      </div>

      {tab === "dashboard" && (
        <div data-testid="occ-dashboard">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center mb-4" data-testid="occ-total-checked">
            <p className="text-[#A19DB0] text-[11px] uppercase tracking-[0.3em]">Total checked</p>
            <p className="font-display text-6xl font-bold mt-2 leading-none"><span className="text-[#4ADE80]">{stats?.checked_in ?? live.checked_in ?? 0}</span><span className="text-[#5A5568]"> / {stats?.signups ?? live.signups ?? 0}</span></p>
          </div>
          <p className="text-[#D4AF37] text-[11px] uppercase tracking-[0.3em]">{stats?.company || "Darkchata"}</p>
          <div className="grid grid-cols-2 gap-4 mt-3">
            {[["Walk-ins", stats?.walk_ins ?? 0, "stat-walkins", "#5CC8FF"], ["No-shows", stats?.no_shows ?? 0, "stat-noshows", "#E57373"]].map(([l, v, tid, col]) => (
              <div key={tid} data-testid={tid} className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <p className="text-[#A19DB0] text-xs">{l}</p>
                <p className="font-display text-3xl font-bold mt-1" style={{ color: col }}>{v}</p>
              </div>
            ))}
          </div>
          {stats?.by_type?.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5 mt-4" data-testid="occ-by-type">
              <p className="text-[#A19DB0] text-[11px] uppercase tracking-[0.3em] mb-3">Checked in by ticket type</p>
              <div className="space-y-2">
                {stats.by_type.map((t, i) => (
                  <div key={i} data-testid={`by-type-${i}`} className="flex items-center justify-between text-sm">
                    <span className="text-[#E8E4F0]">{t.label}</span>
                    <span className="font-semibold"><span className="text-[#4ADE80]">{t.checked_in}</span><span className="text-[#5A5568]"> / {t.total}</span></span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5 mt-4">
            <p className="text-[#A19DB0] text-xs">When · where</p>
            <p className="mt-1">{fmtDateTime(stats?.starts_at)}{stats?.duration_min ? ` · ${stats.duration_min} min` : ""}{stats?.location ? ` · ${stats.location}` : ""}{stats?.instructor ? ` · ${stats.instructor}` : ""}</p>
          </div>
          <h3 className="font-display text-lg font-semibold mt-6 mb-2">Staff activity</h3>
          {(!stats || stats.staff_activity.length === 0) ? <p className="text-[#A19DB0] text-sm">No check-ins recorded yet.</p> : (
            <div className="space-y-2">
              {stats.staff_activity.map((s, i) => (
                <div key={s.name} data-testid={`staff-activity-${i}`} className="flex items-center justify-between rounded-2xl bg-white/5 border border-white/10 p-3">
                  <span className="text-sm font-medium">{s.name}</span>
                  <span className="text-sm text-[#9D4EDD] font-semibold">{s.count} check-in{s.count !== 1 ? "s" : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "scanner" && (
        <div className="grid lg:grid-cols-2 gap-6" data-testid="occ-scanner">
          <div>
            <div className="relative rounded-2xl overflow-hidden bg-black/40 border border-white/10 h-[62vh]">
              <div id="reader" className="w-full h-full" />
              {!scanning && <span className="absolute inset-0 flex items-center justify-center text-[#A19DB0] text-sm pointer-events-none px-6 text-center">Tap “Start camera” — your browser will ask for camera (and flash) permission the first time.</span>}
            </div>
            {!scanning
              ? <button onClick={start} data-testid="scanner-start" className="w-full mt-3 flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-semibold rounded-full py-3 active:scale-[0.98] transition-transform"><Camera size={18} /> Start camera</button>
              : (
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <button onClick={toggleTorch} data-testid="scanner-torch" className={`flex items-center justify-center gap-1.5 rounded-full py-2.5 text-sm font-semibold transition-colors ${torchOn ? "bg-[#D4AF37] text-black" : "bg-white/5 border border-white/10"}`}><Flashlight size={16} /> Torch</button>
                  <button onClick={switchCamera} data-testid="scanner-switch" className="flex items-center justify-center gap-1.5 rounded-full py-2.5 text-sm font-semibold bg-white/5 border border-white/10"><SwitchCamera size={16} /> Flip</button>
                  <button onClick={stop} data-testid="scanner-stop" className="flex items-center justify-center gap-1.5 rounded-full py-2.5 text-sm font-semibold bg-white/5 border border-white/10 text-[#E57373]"><CameraOff size={16} /> Stop</button>
                </div>
              )}
            <p className="text-[#A19DB0] text-xs mt-2">Scan a member's personal QR to check them in and view their passes.</p>
          </div>

          <div>
            <label className="text-xs text-[#A19DB0]">Manual check-in — search member</label>
            <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 mt-1">
              <Search size={18} className="text-[#A19DB0]" />
              <input value={q} onChange={(e) => search(e.target.value)} data-testid="scanner-search" placeholder="Search name or email" className="bg-transparent outline-none flex-1 text-sm placeholder:text-white/30" />
            </div>
            <div className="space-y-2 mt-3">
              {results.map((m) => (
                <div key={m.id} data-testid={`scan-member-${m.id}`} className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3">
                  <img src={avatarSrc(m)} alt="" className="w-9 h-9 rounded-full object-cover" />
                  <div className="flex-1 min-w-0"><p className="font-medium truncate text-sm">{m.name}</p><p className="text-[#A19DB0] text-xs truncate">{m.email}</p></div>
                  <button onClick={() => doCheckin(m.id, m.name)} data-testid={`scan-checkin-${m.id}`} className="flex items-center gap-1 bg-[#D4AF37] text-black rounded-full px-3 py-1.5 text-sm font-semibold active:scale-95 transition-transform"><UserCheck size={15} /> In</button>
                  <button onClick={() => loadPasses(m.id)} data-testid={`scan-passes-${m.id}`} className="flex items-center gap-1 bg-white/10 border border-white/10 rounded-full px-3 py-1.5 text-sm active:scale-95 transition-transform"><Ticket size={15} /></button>
                </div>
              ))}
            </div>
            <h3 className="font-display text-lg font-semibold mt-6 mb-2">Recent check-ins</h3>
            {recent.length === 0 ? <p className="text-[#A19DB0] text-sm">No check-ins yet this session.</p> : (
              <div className="space-y-2">
                {recent.map((r, i) => (
                  <div key={`${r.at}-${i}`} data-testid="recent-checkin" className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-3">
                    <CheckCircle2 size={18} className="text-[#9D4EDD]" />
                    <div className="flex-1"><p className="text-sm font-medium">{r.name}</p><p className="text-[#A19DB0] text-xs">{new Date(r.at).toLocaleTimeString()} {r.guest ? "· guest" : ""}</p></div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {memberPasses && (() => {
            const toScan = memberPasses.passes.filter((p) => !p.checked_in);
            const done = memberPasses.passes.filter((p) => p.checked_in);
            const list = passTab === "scanned" ? done : toScan;
            return (
            <div data-testid="member-pass-panel" className="lg:col-span-2 rounded-2xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.04] p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-lg font-semibold">{memberPasses.member.name}'s passes</h3>
                <button onClick={() => setMemberPasses(null)} data-testid="close-pass-panel" className="text-[#A19DB0] text-sm">Close</button>
              </div>
              <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1 mb-4 w-full max-w-xs">
                {[["passes", `Passes (${toScan.length})`], ["scanned", `Scanned (${done.length})`]].map(([k, lbl]) => (
                  <button key={k} onClick={() => setPassTab(k)} data-testid={`pass-tab-${k}`} className={`flex-1 rounded-full py-1.5 text-sm font-semibold transition-colors ${passTab === k ? "bg-[#D4AF37] text-black" : "text-[#A19DB0]"}`}>{lbl}</button>
                ))}
              </div>
              {list.length === 0 ? <p className="text-[#A19DB0] text-sm">{passTab === "scanned" ? "No passes scanned in yet." : "All passes scanned — nothing left."}</p> : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {list.map((p) => (
                    <div key={p.id} data-testid={`scan-pass-${p.id}`} className="rounded-xl bg-white/5 border border-white/10 p-4">
                      <p className="font-medium">{p.package_name}{p.bundle_size > 1 ? ` · Pass ${p.bundle_index} of ${p.bundle_size}` : ""}{p.credits_total > 1 ? ` · ${p.credits_left}/${p.credits_total} entries` : ""}{p.option ? ` · ${p.option}` : ""}</p>
                      <p className="text-[#A19DB0] text-xs mt-0.5">{p.group}{p.drink_total > 0 ? ` · ${p.drink_left}/${p.drink_total} drinks` : ""}</p>
                      {(p.social_date || (p.event_dates || []).length > 0) && <p data-testid={`scan-pass-date-${p.id}`} className={`text-xs mt-0.5 ${[p.social_event_id, ...(p.event_ids || [])].includes(selected?.ref_id) ? "text-[#4ADE80]" : "text-[#E57373]"}`}>For {p.social_date ? `${p.social_title ? `${p.social_title} · ` : ""}${fmtDate(p.social_date)}` : p.event_dates.map((e) => `${e.title} · ${fmtDate(e.starts_at)}`).join(", ")}{[p.social_event_id, ...(p.event_ids || [])].includes(selected?.ref_id) ? "" : " · wrong event date"}</p>}
                      <div className="flex gap-2 mt-3">
                        {!p.checked_in
                          ? <button onClick={() => passCheckin(p.id)} data-testid={`pass-checkin-${p.id}`} className="flex-1 bg-[#D4AF37] text-black rounded-full py-2 text-sm font-semibold active:scale-95 transition-transform">Scan in</button>
                          : <div className="flex-1 flex items-center gap-2">
                              <span className="flex-1 text-center text-[#4ADE80] text-sm font-semibold py-2 flex items-center justify-center gap-1"><CheckCircle2 size={15} /> Scanned</span>
                              <button onClick={() => passUndo(p.id)} data-testid={`pass-undo-${p.id}`} className="bg-white/10 border border-white/10 rounded-full px-3 py-2 text-sm active:scale-95 transition-transform">Undo</button>
                            </div>}
                        {p.drink_total > 0 && <button onClick={() => redeemDrink(p.id)} disabled={p.drink_left <= 0} data-testid={`redeem-drink-${p.id}`} className="flex items-center gap-1 bg-white/10 border border-white/10 rounded-full px-3 py-2 text-sm disabled:opacity-50 active:scale-95 transition-transform"><Wine size={14} /></button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            );
          })()}
        </div>
      )}

      {tab === "drinks" && (
        <div data-testid="occ-drinks">
          <p className="text-[#A19DB0] text-sm mb-4 flex items-center gap-2"><Wine size={16} className="text-[#D4AF37]" /> This event's tickets include drink coupons. Scan a member's QR or search to redeem their drinks.</p>
          <DrinkScanner embedded />
        </div>
      )}

      {tab === "doorlist" && (
        <div data-testid="occ-doorlist">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3" />
            <div className="flex items-center gap-3">
              <button onClick={refreshRoster} data-testid="roster-refresh" className="text-[#A19DB0] hover:text-white flex items-center gap-1 text-sm"><RefreshCw size={14} /> Refresh</button>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 mb-3">
            <Search size={16} className="text-[#A19DB0]" />
            <input value={rosterQuery} onChange={(e) => setRosterQuery(e.target.value)} data-testid="doorlist-search" placeholder="Search name or email…" className="bg-transparent outline-none flex-1 text-sm placeholder:text-white/30" />
          </div>
          {(() => {
            const STAFF = ["instructor", "artist", "admin", "super_admin"];
            const q = rosterQuery.trim().toLowerCase();
            let rows = staffOnly ? (roster?.rows || []).filter((r) => STAFF.includes(r.role)) : (roster?.rows || []);
            if (q) rows = rows.filter((r) => (r.name || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q));
            if (!roster || rows.length === 0) return <p className="text-[#A19DB0] text-sm">{staffOnly ? "No staff in this list." : "No sign-ups or check-ins yet."}</p>;
            return (
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.user_id} data-testid={`roster-row-${r.user_id}`} className={`flex items-center gap-3 rounded-2xl border p-3 ${r.present ? "bg-[#9D4EDD]/10 border-[#9D4EDD]/30" : "bg-white/5 border-white/10"}`}>
                  <img src={avatarSrc(r)} alt="" className="w-9 h-9 rounded-full object-cover" />
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate flex items-center gap-1.5">{r.name} {r.gender && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/10 text-[#A19DB0]">{r.gender}</span>} <RoleBadge role={r.role} size="xs" /></p><p className="text-[#A19DB0] text-xs">{r.booked ? "Signed up" : "Walk-in guest"}</p></div>
                  {r.present
                    ? <button onClick={() => unCheckin(r.user_id, r.name)} data-testid={`roster-uncheckin-${r.user_id}`} className="flex items-center gap-1 text-[#9D4EDD] text-xs font-semibold hover:text-[#E57373] transition-colors"><CheckCircle2 size={15} /> Here <span className="text-[10px] opacity-70">· undo</span></button>
                    : r.no_show
                      ? <button onClick={() => doCheckin(r.user_id, r.name)} data-testid={`roster-checkin-${r.user_id}`} className="bg-[#D4AF37] text-black rounded-full px-3 py-1 text-xs font-semibold active:scale-95 transition-transform">Check in</button>
                      : <button onClick={() => doCheckin(r.user_id, r.name)} data-testid={`roster-checkin-${r.user_id}`} className="bg-[#D4AF37] text-black rounded-full px-3 py-1 text-xs font-semibold active:scale-95 transition-transform">Check in</button>}
                </div>
              ))}
            </div>
            );
          })()}
        </div>
      )}
      {tab === "holders" && (
        <div data-testid="occ-holders">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold bg-[#9D4EDD]/20 text-[#c79bff] px-3 py-1 rounded-full">{holders?.marked_count ?? 0} marked</span>
              <span className="text-sm text-[#A19DB0]">{holders?.total ?? 0} social-pass holders</span>
            </div>
            <button onClick={loadHolders} data-testid="holders-refresh" className="text-[#A19DB0] hover:text-white flex items-center gap-1 text-sm"><RefreshCw size={14} /> Refresh</button>
          </div>
          <p className="text-[11px] text-[#A19DB0] mb-3">Everyone holding an active social pass. Mark or unmark them for this social night.</p>
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 mb-3">
            <Search size={15} className="text-[#A19DB0]" />
            <input value={holdersQuery} onChange={(e) => setHoldersQuery(e.target.value)} data-testid="holders-search" placeholder="Search name or email…" className="bg-transparent outline-none flex-1 text-sm placeholder:text-white/30" />
          </div>
          {(() => {
            const q = holdersQuery.trim().toLowerCase();
            let rows = holders?.rows || [];
            if (q) rows = rows.filter((r) => (r.name || "").toLowerCase().includes(q) || (r.email || "").toLowerCase().includes(q));
            if (!holders || rows.length === 0) return <p className="text-[#A19DB0] text-sm">No members hold an active social pass.</p>;
            return (
              <div className="space-y-2">
                {rows.map((r) => (
                  <div key={r.user_id} data-testid={`holder-row-${r.user_id}`} className={`flex items-center gap-3 rounded-2xl border p-3 ${r.marked ? "bg-[#9D4EDD]/10 border-[#9D4EDD]/30" : "bg-white/5 border-white/10"}`}>
                    <img src={avatarSrc(r)} alt="" className="w-9 h-9 rounded-full object-cover" />
                    <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate flex items-center gap-1.5">{r.name} {r.gender && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/10 text-[#A19DB0]">{r.gender}</span>}</p><p className="text-[#A19DB0] text-xs">{r.pass_count} social pass{r.pass_count !== 1 ? "es" : ""}</p></div>
                    {r.marked
                      ? <button onClick={() => toggleHolder(r.user_id, r.name, true)} data-testid={`holder-unmark-${r.user_id}`} className="flex items-center gap-1 text-[#9D4EDD] text-xs font-semibold hover:text-[#E57373] transition-colors"><CheckCircle2 size={15} /> Marked <span className="text-[10px] opacity-70">· undo</span></button>
                      : <button onClick={() => toggleHolder(r.user_id, r.name, false)} data-testid={`holder-mark-${r.user_id}`} className="bg-[#D4AF37] text-black rounded-full px-3 py-1 text-xs font-semibold active:scale-95 transition-transform">Mark</button>}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

const SCANNER_CATS = ["classes", "socials", "events"];
